<template>
  <div class="about-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('about.title') }}</h1>
        <p class="page-subtitle">{{ t('about.subtitle') }}</p>
      </div>
    </section>

    <!-- Mission Section -->
    <section class="mission-section">
      <div class="container">
        <h2>{{ t('about.mission') }}</h2>
        <p>{{ t('about.missionText') }}</p>
      </div>
    </section>

    <!-- Vision Section -->
    <section class="vision-section">
      <div class="container">
        <h2>{{ t('about.vision') }}</h2>
        <p>{{ t('about.visionText') }}</p>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <h2>{{ t('about.features') }}</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🎮</div>
            <h3>{{ t('about.feature1.title') }}</h3>
            <p>{{ t('about.feature1.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🌍</div>
            <h3>{{ t('about.feature2.title') }}</h3>
            <p>{{ t('about.feature2.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📱</div>
            <h3>{{ t('about.feature3.title') }}</h3>
            <p>{{ t('about.feature3.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔒</div>
            <h3>{{ t('about.feature4.title') }}</h3>
            <p>{{ t('about.feature4.desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Games Section -->
    <section class="games-section">
      <div class="container">
        <h2>{{ t('about.ourGames') }}</h2>
        <div class="games-grid">
          <div class="game-card">
            <div class="game-emoji">♟️</div>
            <h3>{{ t('games.poker.title') }}</h3>
            <p>{{ t('games.poker.description') }}</p>
            <button @click="goToGame('poker')" class="game-btn">{{ t('buttons.play') }}</button>
          </div>
          <div class="game-card">
            <div class="game-emoji">🔢</div>
            <h3>{{ t('games.sudoku.title') }}</h3>
            <p>{{ t('games.sudoku.description') }}</p>
            <button @click="goToGame('sudoku')" class="game-btn">{{ t('buttons.play') }}</button>
          </div>
          <div class="game-card">
            <div class="game-emoji">🧩</div>
            <h3>{{ t('games.riddles.title') }}</h3>
            <p>{{ t('games.riddles.description') }}</p>
            <button @click="goToGame('riddles')" class="game-btn">{{ t('buttons.play') }}</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section">
      <div class="container">
        <h2>{{ t('about.getInTouch') }}</h2>
        <p>{{ t('about.getInTouchText') }}</p>
        <div class="contact-links">
          <a href="#/contact" class="contact-btn">{{ t('nav.contact') }}</a>
          <a href="#/support" class="contact-btn secondary">{{ t('nav.support') }}</a>
        </div>
      </div>
    </section>

    <!-- Back Button -->
    <div class="container">
      <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()

const goToGame = (game) => {
  window.location.hash = `#/games/${game}`
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.about-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Sections */
.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

section > .container {
  padding-bottom: var(--space-4xl);
}

section > .container h2 {
  font-size: var(--font-size-h3);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
  text-align: center;
}

section > .container > p {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin: 0;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}

/* Mission & Vision Sections */
.mission-section {
  background: var(--color-bg-card);
}

.vision-section {
  background: var(--color-bg-light);
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--space-2xl);
  margin-top: var(--space-2xl);
}

.feature-card {
  background: var(--color-bg-light);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-base) var(--ease-in-out));
}

.feature-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px);
}

.feature-icon {
  font-size: 48px;
  margin-bottom: var(--space-lg);
  display: block;
}

.feature-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-sm) 0;
  font-weight: var(--font-weight-bold);
}

.feature-card p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

/* Games Section */
.games-section {
  background: var(--color-bg-card);
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-2xl);
  margin-top: var(--space-2xl);
}

.game-card {
  background: var(--color-bg-light);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 2px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-base) var(--ease-in-out));
}

.game-card:hover {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-lg);
}

.game-emoji {
  font-size: 56px;
  margin-bottom: var(--space-lg);
  display: block;
}

.game-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
}

.game-card p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg) 0;
  line-height: 1.6;
}

.game-btn {
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.game-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(255, 107, 53, 0.3);
}

/* Contact Section */
.contact-section {
  background: var(--gradient-primary);
  color: white;
  text-align: center;
}

.contact-section h2 {
  color: white;
}

.contact-section p {
  color: rgba(255, 255, 255, 0.95);
}

.contact-links {
  display: flex;
  gap: var(--space-lg);
  justify-content: center;
  margin-top: var(--space-2xl);
  flex-wrap: wrap;
}

.contact-btn {
  display: inline-flex;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-primary-orange);
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  border: 2px solid white;
  font-size: var(--font-size-body);
  cursor: pointer;
}

.contact-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.contact-btn.secondary {
  background: transparent;
  color: white;
  border-color: white;
}

.contact-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Back Button */
.btn-back {
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
  margin: 0 auto var(--space-4xl);
  display: block;
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  section > .container {
    padding-bottom: var(--space-2xl);
  }

  section > .container h2 {
    font-size: var(--font-size-h4);
    margin-bottom: var(--space-lg);
  }

  .features-grid,
  .games-grid {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .feature-card,
  .game-card {
    padding: var(--space-lg);
  }

  .contact-links {
    flex-direction: column;
    gap: var(--space-md);
  }

  .contact-btn {
    flex: 1;
  }

  .btn-back {
    width: 100%;
  }
}

/* Dark Mode Overrides - Fix text visibility */
html.dark-mode .page-header {
  background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
}

html.dark-mode .contact-section {
  background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
}
</style>
